import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  TextInput,
  Flex,
  Box,
  Icon,
  IconName,
  Text,
  Image,
} from '@televet/kibble-ui';
import debounce from 'lodash-es/debounce';
import React, { useCallback, useEffect } from 'react';
import FetchLoader, { FetLoaderSize } from 'shared/components/FetchLoader';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  DocumentationCategory,
  DocumentationWhereInput,
  QueryMode,
  useFindDocumentationLazyQuery,
} from 'shared/types/graphql';
import vinLogo from 'assets/images/vin.png';

const getBackgroundColor = (
  currentCategory: DocumentationCategory | undefined,
  category: DocumentationCategory,
): string => {
  return !currentCategory && category === ('All' as DocumentationCategory)
    ? 'background.info'
    : currentCategory === category
    ? 'background.info'
    : 'transparent';
};

const getHoverBackgroundColor = (
  currentCategory: DocumentationCategory | undefined,
  category: DocumentationCategory,
): string => {
  return !currentCategory && category === ('All' as DocumentationCategory)
    ? 'background.info'
    : currentCategory === category
    ? 'background.info'
    : 'background.secondaryAlpha.hover';
};

const mapCategoryToIcon = (category: DocumentationCategory): IconName => {
  switch (category) {
    case DocumentationCategory.Dogs:
      return 'animalDog';
    case DocumentationCategory.Cats:
      return 'animalCat';
    case DocumentationCategory.Horses:
      return 'animalHorse';
    case DocumentationCategory.Birds:
      return 'animalBird';
    case DocumentationCategory.Ruminants:
      return 'animalCow';
    case DocumentationCategory.SmallMammals:
      return 'animalMouse';
    case DocumentationCategory.Pigs:
      return 'animalPig';
    case DocumentationCategory.Reptiles:
      return 'animalSnake';
    case DocumentationCategory.GeneralInformation:
      return 'infoCircle';
    case DocumentationCategory.Medications:
      return 'pill';
    default:
      return 'paw';
  }
};

const mapCategoryToName = (category: DocumentationCategory): string => {
  switch (category) {
    case DocumentationCategory.Dogs:
      return 'Dogs';
    case DocumentationCategory.Cats:
      return 'Cats';
    case DocumentationCategory.Horses:
      return 'Horses';
    case DocumentationCategory.Birds:
      return 'Birds';
    case DocumentationCategory.Ruminants:
      return 'Ruminants';
    case DocumentationCategory.SmallMammals:
      return 'Small Mammals';
    case DocumentationCategory.Pigs:
      return 'Pigs';
    case DocumentationCategory.Reptiles:
      return 'Reptiles';
    case DocumentationCategory.GeneralInformation:
      return 'General Information';
    case DocumentationCategory.Medications:
      return 'Medications';
    default:
      return 'All';
  }
};

const AttachDocumentationModal = ({
  setDraftMessageText,
  isOpen,
  onClose,
}: {
  setDraftMessageText: (value: React.SetStateAction<string>) => void;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}): JSX.Element => {
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [currentCategory, setCurrentCategory] = React.useState<DocumentationCategory>();
  const [findDocumentation, { data: documentationData, loading: documentationLoading }] = useFindDocumentationLazyQuery(
    {
      fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    },
  );

  const resetState = (): void => {
    setSearchTerm('');
    setCurrentCategory(undefined);
  };

  useEffect(() => {
    if (isOpen && searchTerm) {
      const where: DocumentationWhereInput = { title: { contains: searchTerm, mode: QueryMode.Insensitive } };
      if (currentCategory) {
        where.category = { equals: currentCategory };
      }

      findDocumentation({ variables: { where } });
    }
  }, [currentCategory, searchTerm, findDocumentation, isOpen]);

  const articleCategories = ['All', ...Object.keys(DocumentationCategory)];
  const debouncedSetSearchTerm = debounce((value) => setSearchTerm(value), 350);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      debouncedSetSearchTerm(e.target.value);
    },
    [debouncedSetSearchTerm],
  );

  const handleOpen = useCallback((url: string): void => {
    window.open(url, '_blank');
  }, []);

  const handleAdd = useCallback(
    (article: string, url: string): void => {
      setDraftMessageText(
        (currentValue) =>
          `${currentValue}${
            currentValue.trim() ? '\n' : ''
          }Here you can find more information about ${article.trim()};\n${url}`,
      );
      resetState();
      onClose();
    },
    [onClose, setDraftMessageText],
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={(): void => {
          resetState();
          onClose();
        }}
        isCentered
        size="5xl"
        motionPreset="none"
      >
        <ModalCloseButton />
        <ModalHeader>
          Attach Documentation <Text size="xs">Powered by</Text>
          <Image src={vinLogo} width="9" />
        </ModalHeader>

        <ModalBody display="flex" flexDirection="row" maxHeight="65vh">
          <Flex flexDirection="column" width="250px" overflow="scroll">
            {articleCategories.map((category, categoryIndex) => {
              return (
                <Flex
                  key={`documentation-category-${categoryIndex}`}
                  padding="14px 0 14px 24px"
                  justifyContent="flex-start"
                  alignItems="center"
                  cursor="pointer"
                  _hover={{
                    backgroundColor: getHoverBackgroundColor(currentCategory, category as DocumentationCategory),
                  }}
                  backgroundColor={getBackgroundColor(currentCategory, category as DocumentationCategory)}
                  onClick={(): void => {
                    if (category === 'All') {
                      setCurrentCategory(undefined);
                    } else {
                      setCurrentCategory(category as DocumentationCategory);
                    }
                  }}
                >
                  <Icon name={mapCategoryToIcon(category as DocumentationCategory)} />
                  &nbsp; {mapCategoryToName(category as DocumentationCategory)}
                </Flex>
              );
            })}
          </Flex>
          <Flex flexDirection="column" flex="1" padding="0 12px">
            <TextInput
              placeholder="Search"
              leftElement={<Icon name="magnifyingGlass" />}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <Text size="sm" margin="12px 0 0 4px">
                {documentationData?.findManyDocumentation?.length || 0} Articles
              </Text>
            )}
            {documentationLoading ? (
              <Flex flex="1" justifyContent="center" alignItems="center">
                <FetchLoader size={FetLoaderSize.Large} />
              </Flex>
            ) : searchTerm ? (
              <Flex flexDirection="column" flex="1" overflowY="scroll" margin="0 0 12px 0">
                {documentationData?.findManyDocumentation?.map((document, documentIndex) => {
                  return (
                    <Flex
                      key={documentIndex}
                      justifyContent="space-between"
                      alignItems="center"
                      padding="16px 12px"
                      backgroundColor="background.subtle"
                      margin={1}
                    >
                      <Box width="70%">{document.title}</Box>
                      <Box>
                        <Button size="xs" variant="secondary" onClick={(): void => handleOpen(document.url)}>
                          <Icon name="externalLink" size="sm" />
                          &nbsp;Open
                        </Button>
                        &nbsp;
                        <Button
                          size="xs"
                          variant="primary"
                          onClick={(): void => handleAdd(document.title, document.url)}
                        >
                          <Icon name="plus" size="sm" />
                          &nbsp;Add
                        </Button>
                      </Box>
                    </Flex>
                  );
                })}
                {documentationData?.findManyDocumentation?.length === 0 && (
                  <Flex flex="1" justifyContent="center" alignItems="center" flexDirection="column">
                    <Icon name="magnifyingGlass" size="2xl" />

                    <Text>Whoops! could not find what you are looking for...</Text>
                  </Flex>
                )}
              </Flex>
            ) : (
              <Flex flex="1" justifyContent="center" alignItems="center">
                Your Results will appear here
              </Flex>
            )}
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};

export default AttachDocumentationModal;
